import { ScrollView } from "react-native";
import type { AlarmSchedule } from "@visioni/a3s-alarm-schedules";
import { AlarmException } from "@src/services/api/types";
import { View, Text } from "@src/components";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { getLegendStates } from "@src/features/schedules/utils/schedule-utils";
import type { StripeState } from "@src/features/schedules/utils/stripe-pattern";
import { DiagonalStripeSquare } from "./DiagonalStripeSquare";
import { FC, memo } from "react";

interface LegendProps {
  exceptions?: AlarmException[];
  /** Every schedule the viewer can draw from: the current one and any historical ones. */
  schedules?: AlarmSchedule[];
}

export const Legend: FC<LegendProps> = memo(({ exceptions = [], schedules = [] }) => {
  const { theme } = useUnistyles();
  const armedColor = theme.colors.alarmStates.armed as string;
  // Use trackBackground – disarmed segments don't render in the chart, so the visible
  // "disarmed" colour is the track background behind them (disarmed differs from
  // trackBackground in dark mode).
  const disarmedColor = theme.colors.alarmStates.trackBackground as string;
  const textColor = theme.colors.text as string; // Text color for silence

  const states = getLegendStates(exceptions, schedules);

  // Build array of legend items
  const legendItems: Array<{
    id: string;
    label: string;
    color: string;
    isException?: boolean;
    state?: StripeState;
  }> = [
    { id: "armed", label: "Armed", color: armedColor },
    { id: "disarmed", label: "Disarmed", color: disarmedColor },
  ];

  // Add exception legend items if they exist
  if (states.armedException) {
    legendItems.push({
      id: "armed-exception",
      label: "Armed by exception",
      color: armedColor,
      isException: true,
      state: "armed",
    });
  }
  if (states.disarmedException) {
    legendItems.push({
      id: "disarmed-exception",
      label: "Disarmed by exception",
      color: disarmedColor,
      isException: true,
      state: "disarmed",
    });
  }

  // Add silence and override legend items if they exist
  if (states.silence) {
    legendItems.push({
      id: "silence",
      label: "Silence",
      color: textColor,
    });
  }
  if (states.armedOverride) {
    legendItems.push({
      id: "armed-override",
      label: "Armed by override",
      color: armedColor,
      isException: true,
      state: "armed",
    });
  }
  if (states.disarmedOverride) {
    legendItems.push({
      id: "disarmed-override",
      label: "Disarmed by override",
      color: disarmedColor,
      isException: true,
      state: "disarmed",
    });
  }

  return (
    <View>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.contentContainerStyle}
      >
        {legendItems.map((item) => (
          <View flexDirection="row" alignItems="center" gap="md" key={item.id}>
            {item.isException && item.state ?
              <DiagonalStripeSquare state={item.state} size={12} borderRadius={2} />
            : <View
                w={12}
                h={12}
                rounded={2}
                style={{
                  backgroundColor: item.color,
                }}
              />
            }
            <Text textStyle="xs/regular" color="text">
              {item.label}
            </Text>
          </View>
        ))}
      </ScrollView>
    </View>
  );
});

const styles = StyleSheet.create((theme) => ({
  contentContainerStyle: {
    paddingLeft: 0,
    paddingRight: theme.spacing["3xl"],
    gap: theme.spacing["3xl"],
    alignItems: "center",
  },
}));
